import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { AppShell, AuthGate } from "./components/AppShell";
import { DevOutboxPage, ForgotPage, InvitePage, LoginPage, MfaPage, OnboardingPage, ResetPasswordPage, RootRedirect, SignupPage, VerifyEmailPage } from "./pages/auth/AuthPages";
import { SecurityPage } from "./pages/auth/SecurityPage";
import { OverviewPage } from "./pages/workspace/OverviewPage";
import { ConnectionsPage } from "./pages/workspace/ConnectionsPage";
import { FilesPage } from "./pages/workspace/FilesPage";
import { MigrationsPage } from "./pages/workspace/MigrationsPage";
import { NewMigrationPage } from "./pages/workspace/NewMigrationPage";
import { MigrationDetailPage } from "./pages/workspace/MigrationDetailPage";
import { BillingPage } from "./pages/workspace/BillingPage";
import { SettingsPage } from "./pages/workspace/SettingsPage";
import { PartnerHomePage, PartnerReportPage, PartnerTeamPage } from "./pages/partner/PartnerPages";
import { AdminPage } from "./pages/admin/AdminPage";

const root = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-semibold">Page not found</h1>
      <a href="/" className="text-accent mt-2 inline-block underline">
        Go home
      </a>
    </div>
  ),
});

const page = (path: string, component: () => React.ReactNode) => createRoute({ getParentRoute: () => root, path, component });

const index = page("/", RootRedirect);
const login = page("/login", LoginPage);
const signup = page("/signup", SignupPage);
const mfa = page("/mfa", () => (
  <AuthGate allowIncomplete>
    <MfaPage />
  </AuthGate>
));
const verify = page("/verify-email", VerifyEmailPage);
const forgot = page("/forgot", ForgotPage);
const reset = page("/reset-password", ResetPasswordPage);
const invite = page("/invite", InvitePage);
const onboarding = page("/onboarding", () => (
  <AuthGate>
    <OnboardingPage />
  </AuthGate>
));
const security = page("/security", () => (
  <AuthGate allowIncomplete>
    <SecurityPage />
  </AuthGate>
));
const outbox = page("/dev/outbox", DevOutboxPage);

/** Signed-in layout with the sidebar. */
const shell = createRoute({
  getParentRoute: () => root,
  id: "shell",
  component: () => (
    <AuthGate>
      <AppShell />
    </AuthGate>
  ),
});
const child = (path: string, component: () => React.ReactNode) => createRoute({ getParentRoute: () => shell, path, component });

const wsHome = child("/w/$workspaceId", OverviewPage);
const wsConnections = child("/w/$workspaceId/connections", ConnectionsPage);
const wsFiles = child("/w/$workspaceId/files", FilesPage);
const wsMigrations = child("/w/$workspaceId/migrations", MigrationsPage);
const wsNewMigration = child("/w/$workspaceId/migrations/new", NewMigrationPage);
const wsMigration = child("/w/$workspaceId/migrations/$migrationId", MigrationDetailPage);
const wsBilling = child("/w/$workspaceId/billing", BillingPage);
const wsSettings = child("/w/$workspaceId/settings", SettingsPage);
const pHome = child("/p/$partnerId", PartnerHomePage);
const pReport = child("/p/$partnerId/report", PartnerReportPage);
const pTeam = child("/p/$partnerId/team", PartnerTeamPage);
const admin = child("/admin", AdminPage);

const routeTree = root.addChildren([
  index,
  login,
  signup,
  mfa,
  verify,
  forgot,
  reset,
  invite,
  onboarding,
  security,
  outbox,
  shell.addChildren([wsHome, wsConnections, wsFiles, wsMigrations, wsNewMigration, wsMigration, wsBilling, wsSettings, pHome, pReport, pTeam, admin]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

